import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DialogHeader from '../_parts/DialogHeader';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import {
    closeCreateStampDialog,
    getAllStamps,
    saveStamp,
    appendFilesInfo,
    removeFile,
} from '../../../../redux/user/sliceUserStamps';
import DialogFrame from '../_parts/DialogFrame';
import { useEnqueueSnackbar } from '../../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../../Toast/Toast';
import {
    Autocomplete,
    Button,
    DialogActions,
    DialogContent,
    FormControl,
    FormLabel,
    Stack,
    SxProps,
    TextField,
} from '@mui/material';
import { headingFontBold, bodyFont, vars } from '../../../../Theme';
import UploadFile from '../_parts/UploadFile';
import {
    getFileExtension,
    getFileExtensionsFromFormats,
    getFilenameWithoutExtension,
} from '../../../../util/FileUtils';
import { FileInfo } from '../../../../redux/general/files';
import { getProjectFileTypes } from '../../../../redux/project/sliceProjectFileTypes';
import { useParams } from 'react-router-dom';

const formLabelSx: SxProps = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '2px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

const textFieldSx: SxProps = {
    input: { ...bodyFont, py: 1 },
    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            border: `2px solid ${vars.black}`,
        },
    },
};
const ctaButtonSx: SxProps = {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '14px',
    fontFamily: headingFontBold,
    lineHeight: 'normal',
    padding: '9px 12px',
    color: vars.white,
};

const initialMainState: ICreateStampMainState = {
    name: '',
    category: '',
};

const initialErrorState: ICreateStampErrorState = {
    name: false,
    category: false,
    file: false,
};

export default () => {
    const { replicaId } = useParams();
    const { t } = useTranslation('translations', { keyPrefix: 'createStampDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();
    const enqueueSnackbar = useEnqueueSnackbar();

    const isDialogOpen = useSelector(
        (_store: IAppReduxStore) => _store.userStamps.isCreateStampDialogOpen,
    );
    const { categories, loading, error, isStampSaved, fileInfo } = useSelector(
        (_store: IAppReduxStore) => _store.userStamps,
    );
    const fileTypes = useSelector((store: IAppReduxStore) => store.projectFileTypes.data);
    const allowedExtensions = useMemo(() => getFileExtensionsFromFormats(['STAMP']), [fileTypes]);

    useEffect(() => {
        if (!fileTypes?.length) {
            dispatch(getProjectFileTypes(replicaId || '_'));
        }
    }, []);

    useEffect(() => {
        if (fileInfo) {
            setShowErrorState({
                ...showErrorState,
                file: !validFileExtension(fileInfo),
                name: false,
            });

            setMainState({
                ...mainState,
                name: mainState.name || getFilenameWithoutExtension(fileInfo.fileName),
            });
        }
    }, [fileInfo]);

    useEffect(() => {
        if (isStampSaved) {
            enqueueSnackbar(t('toastSuccess'), {
                severity: ToastSeverity.Success,
            });
            onClose();
        }
    }, [isStampSaved]);

    useEffect(() => {
        if (error) {
            enqueueSnackbar(t('toastError'), {
                severity: ToastSeverity.Error,
            });
            onClose();
        }
    }, [error]);

    const [mainState, setMainState] = useState<ICreateStampMainState>(initialMainState);
    const [showErrorState, setShowErrorState] = useState<ICreateStampErrorState>(initialErrorState);

    const handleChange = ({ field, value }: { field: string; value: string | number }) => {
        setShowErrorState({ ...showErrorState, [field]: false });
        setMainState({
            ...mainState,
            [field]: value,
        });
    };

    const resetToInitialState = () => {
        setMainState(initialMainState);
        setShowErrorState(initialErrorState);
    };

    const onClose = () => {
        resetToInitialState();

        dispatch(getAllStamps());
        dispatch(closeCreateStampDialog());
    };

    const validFileExtension = (fileInfo: FileInfo): boolean => {
        const fileExtension = getFileExtension(fileInfo.fileName).toLowerCase();
        return allowedExtensions.some(extension => extension.slice(1) === fileExtension);
    };

    const validateInput = () => {
        setShowErrorState({
            name: !mainState.name,
            category: !mainState.category,
            file: fileInfo === null || !validFileExtension(fileInfo),
        });
        return !!mainState.name && !!mainState.category && !!fileInfo;
    };
    const onCreate = () => {
        if (validateInput()) {
            dispatch(
                saveStamp({
                    ...mainState,
                }),
            );
        }
    };
    return (
        <DialogFrame open={isDialogOpen} customSx={{ maxHeight: '365px' }}>
            <DialogHeader onClose={onClose} dialogTitle={t('dialogTitle')} />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <Stack spacing={'12px'}>
                    <FormControl>
                        <FormLabel sx={formLabelSx}>{t('name')}</FormLabel>
                        <TextField
                            sx={textFieldSx}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                handleChange({ field: 'name', value: e.target.value })
                            }
                            value={mainState.name}
                            error={showErrorState.name}
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel sx={formLabelSx}>{t('category')}</FormLabel>
                        <Autocomplete
                            value={mainState.category}
                            size='small'
                            onChange={(_, newValue: string | null) => {
                                handleChange({ field: 'category', value: newValue });
                            }}
                            onInputChange={(_, newInputValue) => {
                                handleChange({ field: 'category', value: newInputValue });
                            }}
                            id='stamp-categories-autocomplete'
                            options={categories}
                            sx={textFieldSx}
                            renderInput={params => (
                                <TextField {...params} error={showErrorState.category} />
                            )}
                        />
                    </FormControl>
                    <UploadFile
                        showError={showErrorState.file}
                        fileInfo={fileInfo}
                        formLabel={t('fileAttachment')}
                        buttonLabel={t('selectFile')}
                        acceptFileTypes={allowedExtensions.join(',')}
                        appendFilesInfo={appendFilesInfo}
                        removeFile={removeFile}
                        uploadedDocuments={[
                            {
                                universalId: fileInfo?.id,
                                title: fileInfo?.fileName,
                                size: fileInfo?.fileSize,
                            },
                        ]}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1, borderTop: `1px solid ${vars.lightGray}` }}>
                <Button
                    variant={'text'}
                    onClick={onClose}
                    sx={{ ...ctaButtonSx, color: vars.font }}
                    disabled={loading}
                >
                    {t('ctaCancel')}
                </Button>
                <Button
                    variant={'contained'}
                    onClick={onCreate}
                    sx={ctaButtonSx}
                    disabled={loading}
                >
                    {t('ctaCreate')}
                </Button>
            </DialogActions>
        </DialogFrame>
    );
};

export interface ICreateStampMainState {
    name: string;
    category: string;
}

export interface ICreateStampErrorState {
    name: boolean;
    category: boolean;
    file: boolean;
}
